import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface BlockShellProps {
  children: ReactNode;
  /**
   * When true, the shell represents a parent/group-like container (group, collection, carousel).
   * This mainly affects width/layout but shares the same hover/selection affordances.
   */
  isParentLike?: boolean;
  /**
   * Whether this block is currently selected/active in the editor.
   */
  isSelected?: boolean;
  /**
   * Whether this block is currently hovered (via keyboard focus or pointer).
   */
  isHovered?: boolean;
  /**
   * When the active selection is inside this parent/container. Suppresses this shell's hover
   * chrome so nested blocks keep a single clear focus ring.
   */
  isSelectionScope?: boolean;
  /**
   * Whether the block is currently a drop target while dragging.
   */
  isDropHover?: boolean;
  /**
   * When dragging, we hide the original shell to let the overlay preview take over.
   */
  isDragging?: boolean;
  /**
   * When true, use a flat shell treatment for edge-to-edge content.
   */
  fullBleed?: boolean;
  /**
   * When true, upgrade the hover ring to the selected weight (no background tint).
   */
  showHoverAsSelected?: boolean;
  className?: string;
}

export function BlockShell({
  children,
  isParentLike,
  isSelected,
  isHovered,
  isSelectionScope,
  isDropHover,
  isDragging,
  fullBleed,
  showHoverAsSelected,
  className,
}: BlockShellProps) {
  const showHoverState = isHovered && !isSelected;
  const showSelectedState = isSelected;
  const showBackground = !fullBleed;
  const useStrongHoverLook = showHoverState && !isSelectionScope && showHoverAsSelected;

  return (
    <div
      className={cn(
        'relative min-w-0 transition-[box-shadow,background-color,transform,opacity,margin] duration-150 ease-out',
        'ring-2 ring-transparent',
        showBackground && 'rounded-md',
        isParentLike && 'w-full',
        !isSelectionScope && showHoverState && !useStrongHoverLook && 'ring-editor-primary/35',
        (showSelectedState || useStrongHoverLook) && 'ring-editor-primary/70',
        showBackground && showSelectedState && 'bg-editor-primary/7',
        isDropHover && 'ring-editor-primary/60',
        isDragging && 'opacity-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
